import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { orderApi } from '../api/services';
import { PAYMENT_METHODS, StatusBadge, ORDER_STATUS, useOrderHub } from '../hooks/useOrderHub';

export default function OrderDetailPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { data: o, refetch } = useQuery({
    queryKey: ['order', id],
    queryFn: () => orderApi.getById(id!).then((r) => r.data),
  });

  useOrderHub({
    'order-status-changed': () => refetch(),
    'order-cancelled': () => refetch(),
  });

  const cancel = async () => {
    if (!confirm('Siparişi iptal etmek istiyor musunuz?')) return;
    try {
      await orderApi.cancel(id!, 'Müşteri iptal etti');
      toast.success('Sipariş iptal edildi');
      qc.invalidateQueries({ queryKey: ['my-orders'] });
      refetch();
    } catch (e: any) { toast.error(e?.response?.data?.error || 'İptal edilemedi'); }
  };

  if (!o) return <div className="container" style={{ paddingTop: 32 }}><p className="text-muted">Yükleniyor...</p></div>;

  return (
    <div className="container" style={{ paddingTop: 32, paddingBottom: 48, maxWidth: 800 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'monospace' }}>{o.orderNumber}</h1>
        <StatusBadge status={o.status} />
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginBottom: 8 }}>Ürünler</h3>
        {o.items.map((i: any) => (
          <div key={i.productId} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
            <span>{i.quantity} × {i.productName}</span><span>{Number(i.totalPrice).toFixed(2)} ₺</span>
          </div>
        ))}
        <hr style={{ margin: '12px 0', opacity: 0.2 }} />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Ara Toplam</span><span>{Number(o.subTotal).toFixed(2)} ₺</span></div>
        {o.discountAmount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>İndirim {o.couponCode && `(${o.couponCode})`}</span><span>-{Number(o.discountAmount).toFixed(2)} ₺</span></div>}
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Teslimat</span><span>{Number(o.deliveryFee).toFixed(2)} ₺</span></div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: '1.15rem' }}><span>Toplam</span><span>{Number(o.totalAmount).toFixed(2)} ₺</span></div>
        <p className="text-muted" style={{ marginTop: 8, fontSize: '0.85rem' }}>Ödeme: {PAYMENT_METHODS[o.paymentMethod]}</p>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginBottom: 8 }}>Teslimat</h3>
        <p>{o.address.label} — {o.address.street} No:{o.address.buildingNo}{o.address.buildingName ? ` ${o.address.buildingName}` : ''}{o.address.floor ? ` Kat:${o.address.floor}` : ''}{o.address.apartmentNo ? ` Daire:${o.address.apartmentNo}` : ''}, {o.address.district}/{o.address.city}</p>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginBottom: 8 }}>Durum Geçmişi</h3>
        {o.statusHistory.map((h: any, idx: number) => (
          <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
            <span>{ORDER_STATUS[h.status]?.label}{h.note ? ` — ${h.note}` : ''}</span>
            <span className="text-muted">{new Date(h.changedAt).toLocaleString('tr-TR')}</span>
          </div>
        ))}
      </div>

      {o.status === 1 && <button className="btn" onClick={cancel}>Siparişi İptal Et</button>}
    </div>
  );
}
